import { useEffect, useState } from 'react';
import { api } from '@/services/api';
import { useUIStore } from '@/stores/uiStore';

// Full-screen overlay shown when the user lands on /reset-password?token=...
// (the link in the password-reset email). It reads the token from the URL,
// collects a new password, and POSTs to /auth/reset-password. On success it
// cleans the URL back to "/" and opens the login modal.
export function ResetPassword() {
  const openAuthModal = useUIStore((s) => s.openAuthModal);
  const addNotification = useUIStore((s) => s.addNotification);

  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('token') || '';
    setToken(t);
  }, []);

  const backToMap = () => {
    // Strip the /reset-password path + token so a refresh doesn't reopen this,
    // and notify App (which listens for popstate) to unmount this overlay.
    window.history.replaceState(null, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Use at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      const msg =
        (err as { message?: string })?.message ||
        'This reset link is invalid or has expired. Request a new one from the sign-in screen.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-card">
        <div className="mb-4 text-center">
          <div className="text-lg font-bold text-primary-600">TensorMaps</div>
          <h1 className="mt-1 text-base font-semibold text-gray-900">
            {done ? 'Password updated' : 'Set a new password'}
          </h1>
        </div>

        {!token && !done && (
          <p className="text-sm text-gray-600">
            This link is missing its reset token. Please use the most recent
            password-reset email, or request a new one from the sign-in screen.
          </p>
        )}

        {done ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Your password has been changed. You can now sign in with it.
            </p>
            <button
              type="button"
              onClick={() => {
                backToMap();
                openAuthModal('login');
              }}
              className="w-full rounded-lg bg-primary-600 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
            >
              Sign in
            </button>
          </div>
        ) : (
          token && (
            <form onSubmit={onSubmit} className="space-y-3">
              <div>
                <label className="mb-1 block text-sm text-gray-700">New password</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                  placeholder="At least 8 characters"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-700">Confirm password</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                  placeholder="Re-enter your password"
                />
              </div>
              {error && (
                <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg bg-primary-600 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-60"
              >
                {submitting ? 'Updating…' : 'Update password'}
              </button>
            </form>
          )
        )}

        <button
          type="button"
          onClick={() => {
            addNotification({ type: 'info', message: 'Returned to the map.' });
            backToMap();
          }}
          className="mt-4 w-full text-center text-xs text-gray-500 hover:text-gray-700"
        >
          Back to map
        </button>
      </div>
    </div>
  );
}
